import { useInfiniteQuery } from "@tanstack/react-query";
import { FullTask } from "../types/FullTask";
import { ActivityFilterType } from "../types/filterOptionsTypes";
import { TasksTabs } from "../constants/tasksTabs";
import { httpClient } from "@coworker/http-client";
import { Task } from "../../task-management/types/Task";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  CLOSED_TASKS_QUERY_KEY,
  COMPLETED_TASKS_QUERY_KEY,
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
  UPCOMING_TASKS_QUERY_KEY,
} from "../constants/queryKeys";
import { getTasksEndpointUrl } from "../helpers/getTasksEndpointUrl";
import { addTeamFiltersToQueryOptions } from "../helpers/addTeamFiltersToQueryOptions";

// These are only the properties used for filtering, not ordering
export type StrictFilters = Partial<
  Pick<
    ActivityFilterType,
    | "taskType"
    | "createdBy"
    | "assignedTo"
    | "locations"
    | "pickupLocations"
    | "refillLocations"
    | "priority"
    | "page"
    | "sortedBy"
    | "sortedByStart"
    | "quantityType"
    | "includeUpcoming"
  >
>;

const STALE_AFTER = 1000 * 60 * 5;

export type TaskListItem =
  | { type: "v1"; task_data: FullTask }
  | { type: "v2"; task_data: Task };

type TaskQueryResult = {
  list: TaskListItem[];
  totalCount: number;
  filteredCount: number;
  page?: number;
};

const tasksOverviewTabMap: Record<
  TasksTabs,
  { state: string; queryKey: string }
> = {
  [TasksTabs.OPEN]: { state: "open", queryKey: OPEN_TASKS_QUERY_KEY },
  [TasksTabs.IN_PROGRESS]: {
    state: "in_progress",
    queryKey: IN_PROGRESS_TASKS_QUERY_KEY,
  },
  [TasksTabs.UPCOMING]: {
    state: "pending",
    queryKey: UPCOMING_TASKS_QUERY_KEY,
  },
  [TasksTabs.COMPLETED]: {
    state: "completed",
    queryKey: COMPLETED_TASKS_QUERY_KEY,
  },
  [TasksTabs.CLOSED]: { state: "closed", queryKey: CLOSED_TASKS_QUERY_KEY },
};

export const useTasksOverview = (
  filters: StrictFilters,
  activeTab: TasksTabs,
  enabled = true
) => {
  const uid = useUID();
  const { state, queryKey } = tasksOverviewTabMap[activeTab];

  // sorted array of filter values to key the query along with keys
  // it is sorted so the query caching doesn't create multiple queries for the same
  // filter values but in different order
  const filtersIndex = [];
  for (const key in filters) {
    if (filters[key as keyof StrictFilters]) {
      filtersIndex.push(`${key}=${filters[key as keyof StrictFilters]}`);
    }
  }
  filtersIndex.sort();

  const getTasksQueryFunction = async (
    state: string,
    { pageParam = 1 }: { pageParam: string | number }
  ) => {
    let sortedBy = filters.sortedBy!;
    if (state === "completed" || state === "closed") {
      if (filters.sortedBy === "createdAtDesc") {
        sortedBy = "finishedAtDesc";
      } else if (filters.sortedBy === "createdAtAsc") {
        sortedBy = "finishedAtAsc";
      }
    }

    const queryOptions = {
      state: state,
      ...filters,
      page: pageParam as number,
      pageAfter: pageParam as string,
      sortedBy:
        activeTab === TasksTabs.UPCOMING
          ? filters.sortedByStart || "startAsc"
          : sortedBy,
      includeUpcoming: activeTab === TasksTabs.UPCOMING ? true : false,
    };

    await addTeamFiltersToQueryOptions(
      uid,
      queryOptions,
      filters.createdBy || "",
      filters.assignedTo || ""
    );

    const url = `/v1/${getTasksEndpointUrl(queryOptions)}`;
    const result = await httpClient.tasks.get<{ data: TaskQueryResult }>(url);

    return { ...result.data, page: pageParam } as TaskQueryResult;
  };

  const getNextPageParam = (lastPage: TaskQueryResult) => {
    if (lastPage.list && lastPage.list.length > 0) {
      return lastPage.page! + 1;
    }
    return 1;
  };

  return useInfiniteQuery<TaskQueryResult>({
    queryKey: [OVERVIEW_TASKS_QUERY_KEY, queryKey, ...filtersIndex],
    queryFn: async ({ pageParam }) => {
      return getTasksQueryFunction(state, {
        pageParam: pageParam as string | number,
      });
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageParam,
    staleTime: STALE_AFTER,
    enabled,
  });
};
